/**
 * Control Portal - Enhanced Global Alerts Console
 * Platform-wide alerts with escalation workflow, operator remarks, and response history
 */
import { useState, useEffect, useCallback } from 'react';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import {
  Bell, AlertTriangle, Thermometer, Wifi, WifiOff, Battery,
  RefreshCw, Loader2, Search, CheckCircle, XCircle, Clock, Building2,
  ArrowUpCircle, MessageSquare, Eye, History, Send
} from 'lucide-react';

export default function GlobalAlertsPage() {
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({ by_status: {}, by_level: {} });
  const [activeTab, setActiveTab] = useState('open');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [tenants, setTenants] = useState([]);
  
  // Detail dialog
  const [detailDialog, setDetailDialog] = useState({ open: false, alert: null, loading: false });
  const [alertDetail, setAlertDetail] = useState(null);
  
  // Action dialog
  const [actionDialog, setActionDialog] = useState({ open: false, type: '', alert: null });
  const [actionRemarks, setActionRemarks] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (activeTab !== 'all') {
        params.status = activeTab;
      }
      const response = await controlApi.getGlobalAlerts(params);
      setAlerts(response.data);
    } catch (error) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchStats();
    fetchTenants();
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const fetchStats = async () => {
    try {
      const response = await controlApi.getAlertStats();
      setStats(response.data);
    } catch (error) {
        // Silently handled
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await controlApi.getTenants();
      setTenants(response.data);
    } catch (error) {
        // Silently handled
    }
  };

  const openAlertDetail = async (alert) => {
    setDetailDialog({ open: true, alert, loading: true });
    try {
      const response = await controlApi.getAlertDetail(alert.id);
      setAlertDetail(response.data);
    } catch (error) {
      toast.error('Failed to load alert details');
    } finally {
      setDetailDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleAction = async () => {
    if (!actionDialog.alert) return;
    setActionLoading(true);
    
    try {
      const payload = { remarks: actionRemarks };
      
      switch (actionDialog.type) {
        case 'acknowledge':
          await controlApi.acknowledgeAlert(actionDialog.alert.id, payload);
          toast.success('Alert acknowledged');
          break;
        case 'escalate':
          await controlApi.escalateAlert(actionDialog.alert.id, { escalation_reason: actionRemarks });
          toast.success('Alert escalated');
          break;
        case 'resolve':
          await controlApi.resolveAlert(actionDialog.alert.id, payload);
          toast.success('Alert resolved');
          break;
        case 'comment':
          await controlApi.addAlertComment(actionDialog.alert.id, payload);
          toast.success('Comment added');
          break;
      }
      
      setActionDialog({ open: false, type: '', alert: null });
      setActionRemarks('');
      fetchAlerts();
      fetchStats();
      
      // Refresh detail if open
      if (detailDialog.open && detailDialog.alert?.id === actionDialog.alert.id) {
        openAlertDetail(actionDialog.alert);
      }
    } catch (error) {
      toast.error(`Failed to ${actionDialog.type} alert`);
    } finally {
      setActionLoading(false);
    }
  };

  const getSeverityBadge = (level) => {
    const styles = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      warning: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      info: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    };
    return <Badge className={styles[level] || styles.info}>{level}</Badge>;
  };

  const getStatusBadge = (status, escalated) => {
    if (escalated) {
      return <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">escalated</Badge>;
    }
    const styles = {
      open: 'bg-red-500/20 text-red-400 border-red-500/30',
      acknowledged: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      resolved: 'bg-green-500/20 text-green-400 border-green-500/30',
    };
    return <Badge className={styles[status] || styles.open}>{status}</Badge>;
  };

  const getAlertIcon = (type) => {
    const icons = {
      temp_breach: <Thermometer className="w-4 h-4 text-red-400" />,
      device_offline: <WifiOff className="w-4 h-4 text-amber-400" />,
      low_battery: <Battery className="w-4 h-4 text-amber-400" />,
      entitlement_expiring: <Clock className="w-4 h-4 text-blue-400" />,
      route_deviation: <AlertTriangle className="w-4 h-4 text-amber-400" />,
    };
    return icons[type] || <Bell className="w-4 h-4 text-slate-400" />;
  };

  const filteredAlerts = alerts.filter(alert => {
    if (tenantFilter !== 'all' && alert.tenant_id !== tenantFilter) return false;
    if (levelFilter !== 'all' && alert.level !== levelFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        alert.message?.toLowerCase().includes(q) ||
        alert.device_uid?.toLowerCase().includes(q) ||
        alert.tenant_name?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const actionTitles = {
    acknowledge: 'Acknowledge Alert',
    escalate: 'Escalate Alert',
    resolve: 'Resolve Alert',
    comment: 'Add Comment'
  };

  const actionPlaceholders = {
    acknowledge: 'Add acknowledgment notes...',
    escalate: 'Reason for escalation...',
    resolve: 'Resolution details / solution...',
    comment: 'Add your comment...'
  };

  return (
    <div className="p-6 space-y-6 bg-[#05050a] min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-cyan-100 flex items-center gap-3" data-testid="alerts-title">
            <Bell className="w-7 h-7 text-amber-400" />
            Alerts Console
          </h1>
          <p className="text-cyan-600 text-sm font-mono tracking-wide mt-1">MONITOR AND MANAGE ALERTS ACROSS ALL CUSTOMERS</p>
        </div>
        <Button 
          onClick={() => { fetchAlerts(); fetchStats(); }} 
          className="bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20"
          data-testid="refresh-alerts"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          REFRESH
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="alert-stats">
        <button 
          onClick={() => setActiveTab('open')}
          className={`bg-[#0a0a12] border p-4 text-left transition-all ${
            activeTab === 'open' ? 'border-red-500/50 glow-red' : 'border-cyan-500/20 hover:border-cyan-500/40'
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="w-5 h-5 text-red-400" />
            <span className="text-cyan-600 text-xs font-mono">OPEN</span>
          </div>
          <p className="text-2xl font-bold font-mono text-red-400">{stats.by_status?.open || 0}</p>
        </button>
        
        <button 
          onClick={() => setActiveTab('acknowledged')}
          className={`bg-[#0a0a12] border p-4 text-left transition-all ${
            activeTab === 'acknowledged' ? 'border-amber-500/50' : 'border-cyan-500/20 hover:border-cyan-500/40'
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-amber-400" />
            <span className="text-cyan-600 text-xs font-mono">ACKNOWLEDGED</span>
          </div>
          <p className="text-2xl font-bold font-mono text-amber-400">{stats.by_status?.acknowledged || 0}</p>
        </button>
        
        <button 
          onClick={() => setActiveTab('escalated')}
          className={`bg-[#0a0a12] border p-4 text-left transition-all ${
            activeTab === 'escalated' ? 'border-purple-500/50' : 'border-cyan-500/20 hover:border-cyan-500/40'
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <ArrowUpCircle className="w-5 h-5 text-purple-400" />
            <span className="text-cyan-600 text-xs font-mono">ESCALATED</span>
          </div>
          <p className="text-2xl font-bold font-mono text-purple-400">{stats.by_status?.escalated || 0}</p>
        </button>
        
        <button 
          onClick={() => setActiveTab('resolved')}
          className={`bg-[#0a0a12] border p-4 text-left transition-all ${
            activeTab === 'resolved' ? 'border-green-500/50' : 'border-cyan-500/20 hover:border-cyan-500/40'
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-cyan-600 text-xs font-mono">RESOLVED</span>
          </div>
          <p className="text-2xl font-bold font-mono text-green-400">{stats.by_status?.resolved || 0}</p>
        </button>
        
        <div className="bg-[#0a0a12] border border-cyan-500/20 p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <span className="text-cyan-600 text-xs font-mono">CRITICAL</span>
          </div>
          <p className="text-2xl font-bold font-mono text-red-400">{stats.by_level?.critical || 0}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-cyan-600 w-4 h-4" />
          <Input
            placeholder="Search by message, device, or customer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#0a0a12] border-cyan-500/20 text-cyan-100 placeholder:text-cyan-700 focus:border-cyan-500/50"
            data-testid="alert-search"
          />
        </div>
        <Select value={levelFilter} onValueChange={setLevelFilter}>
          <SelectTrigger className="w-40 bg-[#0a0a12] border-cyan-500/20 text-cyan-100" data-testid="level-filter">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent className="bg-[#0a0a12] border-cyan-500/20">
            <SelectItem value="all" className="text-cyan-100">All Levels</SelectItem>
            <SelectItem value="critical" className="text-cyan-100">Critical</SelectItem>
            <SelectItem value="warning" className="text-cyan-100">Warning</SelectItem>
            <SelectItem value="info" className="text-cyan-100">Info</SelectItem>
          </SelectContent>
        </Select>
        <Select value={tenantFilter} onValueChange={setTenantFilter}>
          <SelectTrigger className="w-48 bg-[#0a0a12] border-cyan-500/20 text-cyan-100" data-testid="tenant-filter">
            <SelectValue placeholder="Customer" />
          </SelectTrigger>
          <SelectContent className="bg-[#0a0a12] border-cyan-500/20">
            <SelectItem value="all" className="text-cyan-100">All Customers</SelectItem>
            {tenants.map(t => (
              <SelectItem key={t.id} value={t.id} className="text-cyan-100">{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Alerts Table */}
      <div className="bg-[#0a0a12] border border-cyan-500/20 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-cyan-500/20 bg-[#080810]">
              <TableHead className="text-cyan-500 font-mono text-xs w-12">TYPE</TableHead>
              <TableHead className="text-cyan-500 font-mono text-xs">CUSTOMER</TableHead>
              <TableHead className="text-cyan-500 font-mono text-xs">DEVICE</TableHead>
              <TableHead className="text-cyan-500 font-mono text-xs">MESSAGE</TableHead>
              <TableHead className="text-cyan-500 font-mono text-xs w-24">LEVEL</TableHead>
              <TableHead className="text-cyan-500 font-mono text-xs w-28">STATUS</TableHead>
              <TableHead className="text-cyan-500 font-mono text-xs w-40">TIME</TableHead>
              <TableHead className="text-cyan-500 font-mono text-xs w-32">ACTIONS</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-cyan-500 mx-auto" />
                </TableCell>
              </TableRow>
            ) : filteredAlerts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-cyan-600">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  NO ALERTS FOUND
                </TableCell>
              </TableRow>
            ) : (
              filteredAlerts.map((alert) => (
                <TableRow 
                  key={alert.id} 
                  className="border-cyan-500/10 hover:bg-cyan-500/5"
                  data-testid={`alert-row-${alert.id}`}
                >
                  <TableCell>{getAlertIcon(alert.type)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-cyan-600" />
                      <span className="text-cyan-200">{alert.tenant_name || 'Unknown'}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-cyan-100 font-mono text-sm">
                    {alert.device_uid || '-'}
                  </TableCell>
                  <TableCell className="text-cyan-200 max-w-xs">
                    <p className="truncate">{alert.message}</p>
                    {alert.details && (
                      <p className="text-cyan-600 text-xs truncate mt-1">{alert.details}</p>
                    )}
                  </TableCell>
                  <TableCell>{getSeverityBadge(alert.level)}</TableCell>
                  <TableCell>{getStatusBadge(alert.status, alert.escalated_at)}</TableCell>
                  <TableCell className="text-cyan-500 text-sm font-mono">
                    {alert.triggered_at ? new Date(alert.triggered_at).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-400 hover:text-white"
                        onClick={() => openAlertDetail(alert)}
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {alert.status === 'open' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-amber-400 hover:text-amber-300"
                          onClick={() => setActionDialog({ open: true, type: 'acknowledge', alert })}
                          title="Acknowledge"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                      )}
                      {alert.status !== 'resolved' && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-purple-400 hover:text-purple-300"
                            onClick={() => setActionDialog({ open: true, type: 'escalate', alert })}
                            title="Escalate"
                          >
                            <ArrowUpCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-green-400 hover:text-green-300"
                            onClick={() => setActionDialog({ open: true, type: 'resolve', alert })}
                            title="Resolve"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Alert Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => setDetailDialog({ open, alert: null, loading: false })}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-400" />
              Alert Details
            </DialogTitle>
          </DialogHeader>
          
          {detailDialog.loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : alertDetail && (
            <div className="space-y-4">
              {/* Alert Info */}
              <div className="bg-slate-800 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-white font-medium">{alertDetail.message}</p>
                    <p className="text-slate-400 text-sm mt-1">
                      {alertDetail.tenant_name} • {alertDetail.device_uid || 'No device'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {getSeverityBadge(alertDetail.level)}
                    {getStatusBadge(alertDetail.status, alertDetail.escalated_at)}
                  </div>
                </div>
                
                {alertDetail.details && (
                  <div className="pt-3 border-t border-slate-700">
                    <p className="text-slate-400 text-xs uppercase tracking-wide mb-1">Solution / Details</p>
                    <p className="text-white">{alertDetail.details}</p>
                  </div>
                )}
                
                {alertDetail.escalation_reason && (
                  <div className="pt-3 border-t border-slate-700">
                    <p className="text-slate-400 text-xs uppercase tracking-wide mb-1">Escalation Reason</p>
                    <p className="text-purple-400">{alertDetail.escalation_reason}</p>
                  </div>
                )}
              </div>
              
              {/* Timeline */}
              <div className="bg-slate-800 rounded-lg p-4">
                <p className="text-slate-400 text-xs uppercase tracking-wide mb-3">Timeline</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Triggered</span>
                    <span className="text-white">{new Date(alertDetail.triggered_at).toLocaleString()}</span>
                  </div>
                  {alertDetail.acknowledged_at && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Acknowledged</span>
                      <span className="text-amber-400">{new Date(alertDetail.acknowledged_at).toLocaleString()}</span>
                    </div>
                  )}
                  {alertDetail.escalated_at && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Escalated</span>
                      <span className="text-purple-400">{new Date(alertDetail.escalated_at).toLocaleString()}</span>
                    </div>
                  )}
                  {alertDetail.resolved_at && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Resolved</span>
                      <span className="text-green-400">{new Date(alertDetail.resolved_at).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Response History */}
              <div className="bg-slate-800 rounded-lg p-4">
                <p className="text-slate-400 text-xs uppercase tracking-wide mb-3 flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Response History
                </p>
                {alertDetail.response_history?.length === 0 ? (
                  <p className="text-slate-500 text-sm">No responses yet</p>
                ) : (
                  <div className="space-y-3">
                    {alertDetail.response_history?.map((resp) => (
                      <div key={resp.id} className="border-l-2 border-slate-600 pl-3">
                        <div className="flex items-center gap-2 text-sm">
                          <Badge variant="outline" className="text-xs border-slate-600">
                            {resp.action}
                          </Badge>
                          <span className="text-slate-400">by {resp.user_name}</span>
                          <span className="text-slate-500 text-xs">
                            {new Date(resp.created_at).toLocaleString()}
                          </span>
                        </div>
                        {resp.remarks && (
                          <p className="text-slate-300 text-sm mt-1">{resp.remarks}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Quick Actions */}
              {alertDetail.status !== 'resolved' && (
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1 border-slate-700"
                    onClick={() => {
                      setDetailDialog({ open: false, alert: null, loading: false });
                      setActionDialog({ open: true, type: 'comment', alert: alertDetail });
                    }}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Add Comment
                  </Button>
                  {alertDetail.status === 'open' && (
                    <Button
                      className="flex-1 bg-amber-600 hover:bg-amber-700"
                      onClick={() => {
                        setDetailDialog({ open: false, alert: null, loading: false });
                        setActionDialog({ open: true, type: 'acknowledge', alert: alertDetail });
                      }}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Acknowledge
                    </Button>
                  )}
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      setDetailDialog({ open: false, alert: null, loading: false });
                      setActionDialog({ open: true, type: 'resolve', alert: alertDetail });
                    }}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Resolve
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => {
        setActionDialog({ open, type: '', alert: null });
        setActionRemarks('');
      }}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">
              {actionTitles[actionDialog.type]}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {actionDialog.alert && (
              <div className="bg-slate-800 rounded-lg p-3">
                <p className="text-white text-sm">{actionDialog.alert.message}</p>
                <p className="text-slate-500 text-xs mt-1">
                  {actionDialog.alert.tenant_name} • {actionDialog.alert.device_uid || 'No device'}
                </p>
              </div>
            )}
            
            <div>
              <Label className="text-slate-300">
                {actionDialog.type === 'escalate' ? 'Escalation Reason' : 'Remarks'}
                {actionDialog.type === 'comment' && <span className="text-red-400">*</span>}
              </Label>
              <Textarea
                value={actionRemarks}
                onChange={(e) => setActionRemarks(e.target.value)}
                placeholder={actionPlaceholders[actionDialog.type]}
                className="bg-slate-800 border-slate-700 text-white mt-2"
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setActionDialog({ open: false, type: '', alert: null });
                setActionRemarks('');
              }}
              className="border-slate-700"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAction}
              disabled={actionLoading || (actionDialog.type === 'comment' && !actionRemarks.trim())}
              className={
                actionDialog.type === 'escalate' ? 'bg-purple-600 hover:bg-purple-700' :
                actionDialog.type === 'resolve' ? 'bg-green-600 hover:bg-green-700' :
                'bg-blue-600 hover:bg-blue-700'
              }
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {actionDialog.type === 'acknowledge' ? 'Acknowledge' :
               actionDialog.type === 'escalate' ? 'Escalate' :
               actionDialog.type === 'resolve' ? 'Resolve' : 'Add Comment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
